import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, number, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Building2, Users, HandCoins, TrendingUp, Wallet, AlertTriangle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`} className="stat-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#64748B] font-semibold">{label}</div>
          <div className="text-3xl font-bold tracking-tight text-[#0F172A] mt-2 tabular-nums">{value}</div>
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="w-[18px] h-[18px]" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="nomi-spin" />;
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">Dashboard</h1>
        <p className="text-sm text-[#64748B] mt-1">Vista general del sistema de adelantos de nómina.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi label="Empresas activas" value={number(stats.companies_active)} icon={Building2} accent="bg-[#D1FAE5] text-[#147D5C]" />
        <Kpi label="Empleados" value={number(stats.total_employees)} icon={Users} accent="bg-blue-50 text-blue-700" />
        <Kpi label="Pendientes hoy" value={number(stats.pending_today)} icon={HandCoins} accent="bg-amber-50 text-amber-700" />
        <Kpi label="En circulación" value={money(stats.monto_circulacion)} icon={Wallet} accent="bg-indigo-50 text-indigo-700" />
        <Kpi label="Ingresos del mes" value={money(stats.ingresos_mes)} icon={TrendingUp} accent="bg-[#D1FAE5] text-[#147D5C]" />
      </div>

      {/* Upcoming liquidations alert */}
      {stats.upcoming_liquidations?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 p-5" data-testid="upcoming-alert">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-[18px] h-[18px] text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#78350F]">Liquidaciones próximas (siguientes 3 días)</div>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.upcoming_liquidations.map((u) => (
                  <div key={u.company_id} className="bg-white border border-amber-200 rounded-md p-3">
                    <div className="text-sm font-semibold text-[#0F172A]">{u.company_name}</div>
                    <div className="text-xs text-[#64748B] mt-1">
                      {u.days_left === 0 ? "Hoy" : `En ${u.days_left} día${u.days_left > 1 ? "s" : ""}`} · {formatDate(u.payday)}
                    </div>
                    <div className="text-xs text-[#475569] mt-1">{u.pending_advances} adelantos transferidos por liquidar</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-[#0F172A]">Tendencia de adelantos</h2>
            <p className="text-xs text-[#64748B]">Últimos 6 meses</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={stats.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
            <YAxis tick={{ fontSize: 12, fill: "#64748B" }} />
            <Tooltip
              contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
              formatter={(v, name) => [name === "count" ? v : money(v), name === "amount" ? "Monto" : name === "fees" ? "Fees" : "Cantidad"]}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="amount" name="Monto adelantado" fill="#1D9E75" radius={[4, 4, 0, 0]} />
            <Bar dataKey="fees" name="Fees generados" fill="#94A3B8" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/adelantos" className="stat-card block hover:border-[#1D9E75] group" data-testid="quick-advances">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">Gestión rápida</div>
              <div className="text-lg font-semibold mt-1 text-[#0F172A] group-hover:text-[#1D9E75] transition-colors">Solicitudes de adelanto</div>
              <div className="text-xs text-[#64748B] mt-1">Aprobar, transferir SPEI y monitorear estados</div>
            </div>
            <HandCoins className="w-6 h-6 text-[#1D9E75]" />
          </div>
        </Link>
        <Link to="/liquidaciones" className="stat-card block hover:border-[#1D9E75] group" data-testid="quick-liquidations">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">Cierre de periodo</div>
              <div className="text-lg font-semibold mt-1 text-[#0F172A] group-hover:text-[#1D9E75] transition-colors">Liquidaciones</div>
              <div className="text-xs text-[#64748B] mt-1">Generar descuentos por empresa al día de nómina</div>
            </div>
            <Wallet className="w-6 h-6 text-[#1D9E75]" />
          </div>
        </Link>
      </div>
    </div>
  );
}
